import { classNames } from '@fullstory/util/fmt';
import { Moment } from 'moment';
import React, { ReactNode, useContext } from 'react';
import { ButtonBase } from '../../../components/ButtonBase';
import { DateRangePickerContext } from '../context';
import { MomentRange, MomentRanges, RangeIndex, rangeIndexToColor } from '../types';
import { isSameDay, isWithinRange } from '../util';
import { DayLabel } from './DayLabel';
import css from './index.module.scss';
import { RangeSegment } from './RangeSegment';
import { getDayRangeSegments, getDayState } from './util';

export interface DayProps {
  /**
   * The day that will be rendered, must be a Moment object.
   */
  day: Moment;
  /**
   * The selected/comparison date ranges. The `Day` will be rendered differently
   * based on if/where it falls within the provided ranges.
   */
  ranges: MomentRanges;
  /**
   * Controls the color of the preview range and which range takes priority when
   * rendering overlapping ranges.
   */
  focusedRange: RangeIndex;
  /**
   * Outlined range used to provide a selection preview while selecting a range.
   * Color of the preview outline is determined by which range is currently
   * focused.
   */
  preview?: MomentRange;
  /**
   * Handler that's called when user clicks the day.
   */
  onClick: () => void;
  /**
   * Handler that's called when user mouses over the day.
   */
  onMouseEnter: () => void;
  /**
   * Handler that's called when user mouses out of the day.
   */
  onMouseLeave: () => void;
  /**
   * If `true`, day will be disabled and non-interactive. Useful for days that are
   * outside of the min/max dates or disabled for some other reason.
   */
  disabled?: boolean;
  /**
   * If provided, the day will be highlighted and an info tooltip will be displayed
   * on hover.
   */
  infoTooltip?: ReactNode;
}

const Day: React.FC<DayProps> = ({
  day,
  ranges,
  focusedRange,
  preview,
  onClick,
  onMouseEnter,
  onMouseLeave,
  disabled,
  infoTooltip,
}) => {
  const { color, prominent } = getDayState(day, ranges, focusedRange);
  const rangeSegments = getDayRangeSegments(day, ranges, focusedRange);
  const { readOnly, v2 } = useContext(DateRangePickerContext);
  if (disabled || (readOnly && !rangeSegments.length))
    return <NonInteractiveDay day={day} className={v2 ? css['day-v2'] : css.day} />;
  return (
    <ButtonBase
      data-component="Day"
      testId="date-range-picker-day"
      className={v2 ? css['day-v2'] : css.day}
      onClick={() => onClick()}
      onMouseEnter={() => onMouseEnter()}
      onMouseLeave={() => onMouseLeave()}
      disabled={readOnly}
    >
      <DayLabel day={day} color={color} prominent={prominent} infoTooltip={infoTooltip} />
      {rangeSegments.map(({ key, ...segmentProps }) => (
        <RangeSegment key={key} {...segmentProps} />
      ))}
      {isWithinRange(day, preview) && (
        <RangeSegment
          color={rangeIndexToColor[focusedRange]}
          start={isSameDay(day, preview?.start)}
          end={isSameDay(day, preview?.end)}
          outline
        />
      )}
    </ButtonBase>
  );
};

interface NonInteractiveDayProps extends Pick<DayProps, 'day'> {
  className?: string;
}

const NonInteractiveDay: React.FC<NonInteractiveDayProps> = ({ day, className }) => (
  <ButtonBase
    data-component="NonInteractiveDay"
    testId="date-range-picker-non-interactive-day"
    className={classNames(className, css.disabled)}
    disabled
  >
    <DayLabel day={day} disabled />
  </ButtonBase>
);

const MemoizedDay = React.memo(Day);

export { MemoizedDay as Day };
